import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format time string to remove seconds.
 * Converts "HH:MM:SS" or "HH:MM:SS.mmm" to "HH:MM"
 * Returns null/undefined as-is.
 */
export function formatTime(time: string | null | undefined): string | null | undefined {
  if (!time) return time;
  // Take only the first 5 characters (HH:MM)
  return time.substring(0, 5);
}

// ─── CSV Export ───────────────────────────────────────────────────────

interface CsvColumn<T> {
  header: string;
  accessor: (row: T) => string | number | boolean | null | undefined;
}

/**
 * Generate a CSV string and trigger a browser download.
 */
export function downloadCsv<T>(
  rows: T[],
  columns: CsvColumn<T>[],
  filename: string
) {
  const escape = (val: unknown): string => {
    const str = val == null ? "" : String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const header = columns.map((c) => escape(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escape(c.accessor(row))).join(","))
    .join("\n");

  // BOM for Excel to recognise UTF-8
  const bom = "\uFEFF";
  const blob = new Blob([bom + header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
